import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { IconField } from '@/components/admin/shared/icon-field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { slugify } from '@/lib/slug'
import { trpc } from '@/lib/trpc'
import { Modal } from '../shared/modal'
import type { Source, SourceCategory } from './types'
import { SOURCE_CATEGORY_LABELS } from './types'

interface AddSourceModalProps {
  isOpen: boolean
  onClose: () => void
  onSourceCreated: (source: Source) => void
}

interface FormData {
  name: string
  slug: string
  icon: string
  category: SourceCategory
  url: string
}

export function AddSourceModal({
  isOpen,
  onClose,
  onSourceCreated,
}: AddSourceModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    slug: '',
    icon: '',
    category: 'prep_resource',
    url: '',
  })
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  const [error, setError] = useState('')

  const utils = trpc.useUtils()
  const createMutation = trpc.admin.sources.create.useMutation({
    onSuccess: () => {
      utils.admin.sources.list.invalidate()
    },
  })

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value
    setFormData((prev) => ({
      ...prev,
      name,
      // Auto-generate slug if not manually edited
      slug: slugManuallyEdited ? prev.slug : slugify(name),
    }))
  }

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugManuallyEdited(true)
    setFormData((prev) => ({
      ...prev,
      slug: e.target.value,
    }))
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    if (!formData.category) {
      setError('Category is required')
      return
    }

    try {
      const source = await createMutation.mutateAsync({
        name: formData.name.trim(),
        slug: formData.slug.trim() || undefined,
        icon: formData.icon.trim() || undefined,
        category: formData.category,
        url: formData.url.trim() || undefined,
      })
      toast.success(`Created source "${source.name}"`)
      onSourceCreated(source as Source)
      handleClose()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create source'
      setError(message)
      toast.error(message)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      slug: '',
      icon: '',
      category: 'prep_resource',
      url: '',
    })
    setSlugManuallyEdited(false)
    setError('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Source">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="source-name"
            className="text-muted-foreground mb-1 block text-sm font-medium"
          >
            Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="source-name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="e.g., OpenAI Interview Experience"
            required
          />
        </div>

        <div>
          <label
            htmlFor="source-slug"
            className="text-muted-foreground mb-1 block text-sm font-medium"
          >
            Slug
          </label>
          <Input
            id="source-slug"
            name="slug"
            type="text"
            value={formData.slug}
            onChange={handleSlugChange}
            placeholder="Auto-generated from name"
          />
          <p className="text-muted-foreground mt-1 text-xs">
            URL-friendly identifier. Auto-generated if left empty.
          </p>
        </div>

        <div>
          <label
            htmlFor="source-category"
            className="text-muted-foreground mb-1 block text-sm font-medium"
          >
            Category <span className="text-destructive">*</span>
          </label>
          <select
            id="source-category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            required
          >
            {(
              Object.entries(SOURCE_CATEGORY_LABELS) as [
                SourceCategory,
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <IconField
            label="Icon"
            value={formData.icon}
            onChange={(icon) => setFormData((prev) => ({ ...prev, icon }))}
          />
        </div>

        <div>
          <label
            htmlFor="source-url"
            className="text-muted-foreground mb-1 block text-sm font-medium"
          >
            URL
          </label>
          <Input
            id="source-url"
            name="url"
            type="url"
            value={formData.url}
            onChange={handleChange}
            placeholder="https://..."
          />
          <p className="text-muted-foreground mt-1 text-xs">
            Optional link to the source website
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Source'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
